import '.a{display:flex;}.ssr-plugin'
import '.bm{justify-content:start;}.ssr-plugin'
import '.h{width:100%;}.ssr-plugin'
import '.a1{height:100%;}.ssr-plugin'
import '.i{flex-direction:column;}.ssr-plugin'
import '.v{justify-content:space-between;}.ssr-plugin'
import '.j{gap:16px;}.ssr-plugin'
import '.z{flex:1;}.ssr-plugin'
import '.bl{gap:8px;}.ssr-plugin'
import '.bs{margin-right:4px;}.ssr-plugin'
import '.b{align-items:center;}.ssr-plugin'
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { Text } from './Text'
import { Canva } from './Canva'
import { VoteButton } from './VoteButton'
import { useTheme } from '../providers/useTheme'
import { useIsMobile } from '../hooks/useIsMobile'
import { styled, Style } from 'inlines'
import { formatCurrency } from '@/apps/cms/utils/misc'
import { useAppCtx } from '../providers/useAppCtx'

type ContestantCardVariant = 'vertical' | 'horizontal'

export type ContestantCardProps = {
  label: string
  description: string
  variant?: ContestantCardVariant
  image: string
  isFocused?: boolean
  votes?: number
  maxVotes?: number
  votePrice?: number
  currency?: string
  ddi?: number
  isInverted?: boolean
  isInCart?: boolean
  style?: Style
  on?: {
    change?: (votes: number) => void
  }
}

export const ContestantCard: React.FC<ContestantCardProps> = memo(
  ({
    label,
    description,
    variant = 'vertical',
    image,
    isFocused,
    votes: votesProp = 0,
    ddi,
    isInverted,
    isInCart,
    style,
    on,
  }) => {
    const { maxVotes, price, currency, activeLanguage, votesCount } =
      useAppCtx()
    const { colors } = useTheme()
    const [internalFocused, setInternalFocused] = useState<boolean>(
      Boolean(isFocused),
    )
    const isControlled = typeof votesProp === 'number'
    const [internalVotes, setInternalVotes] = useState<number>(votesProp ?? 0)
    const isMobile = useIsMobile()

    useEffect(() => {
      if (isControlled) {
        setInternalVotes(votesProp)
      }
    }, [isControlled, votesProp])

    const votes = isControlled ? votesProp : internalVotes

    useEffect(() => {
      if (typeof isFocused === 'boolean') {
        setInternalFocused(isFocused)
      }
    }, [isFocused])

    useEffect(() => {
      if (internalVotes === 0) {
        setInternalFocused(false)
      }
    }, [internalVotes])

    const resolvedIsFocused = isFocused ?? internalFocused
    const isHorizontal = variant === 'horizontal'

    const handleVoteChange = useCallback(
      (nextVotes: number) => {
        setInternalVotes(nextVotes)

        if (nextVotes) {
          setInternalFocused(true)
        }
        on?.change?.(nextVotes)
      },
      [on],
    )

    const direction = isHorizontal ? 'row' : 'column'

    const cardAriaLabel = useMemo(
      () => `${label} - ${description}`,
      [label, description],
    )

    const priceLabel = formatCurrency(
      (votesProp * price) / 100,
      currency,
      activeLanguage,
    )

    return (
      <styled.section className="a bm h a1"
        role="group"
        aria-label={cardAriaLabel}
        tabIndex={0}
        style={{
          flexDirection: direction as 'row' | 'column',
          gap: isHorizontal ? 20 : 16,
          ...style,
        }}
      >
        <Canva
          src={image}
          size={
            isInCart && (isHorizontal || isMobile)
              ? {
                  width: 102,
                  height: 102,
                }
              : {
                  width: 'auto',
                  height: 'auto',
                }
          }
          isFocused={resolvedIsFocused}
          ddi={ddi}
          style={{
            opacity: maxVotes === votesCount && !votes ? 0.5 : 1,
            cursor:
              maxVotes === votesCount && !votes ? 'not-allowed' : 'pointer',
          }}
          onClick={() => {
            if (votesCount < maxVotes) {
              handleVoteChange(votes + 1)
            }
          }}
        />

        <div className="a i v h j z"
        >
          <div className="a i h bl"
          >
            <Text
              as="h3"
              color={colors.textPrimary}
              variant="heading"
              isMultiline
              aria-label={label}
              style={{
                userSelect: 'none',
                opacity: maxVotes === votesCount && !votes ? 0.5 : 1,
              }}
            >
              <styled.span className="bs"
                style={{
                  color: colors.primaryAccent,
                  '&:after': {
                    content: "'•'",
                    fontWeight: '400',
                    fontSize: '15px',
                    lineHeight: '16px',
                    position: 'relative',
                    left: '0px',
                  },
                }}
              >
                {ddi}{' '}
              </styled.span>
              {label}
            </Text>

            <Text
              variant="display-regular"
              color={colors.textSecondary}
              isMultiline
              style={{
                userSelect: 'none',
                opacity: maxVotes === votesCount && !votes ? 0.5 : 1,
              }}
            >
              {description}
            </Text>
          </div>

          <footer className="h a v b bl"
          >
            <VoteButton
              isInverted={isInverted}
              maxVotes={maxVotes}
              votes={votes}
              on={{
                change: handleVoteChange,
              }}
            />

            {isHorizontal && price && (
              <Text
                variant="subheading-regular"
                color={colors.textPrimary}
                aria-label={priceLabel}
              >
                {priceLabel}
              </Text>
            )}
          </footer>
        </div>
      </styled.section>
    )
  },
)
